import { Link } from "@tanstack/react-router";
import { ArrowRight, ArrowUpRight, Bot, Check, ListChecks } from "lucide-react";
import { useEffect, useRef, type ReactNode } from "react";
import { Button, Callout, Skeleton } from "@/design";
import { useMyOnboarding, useWorkspace } from "@/lib/api";
import { GitHubMark } from "@/routes/$workspace/-new/GitHubMark";

/** A short, quiet "done" moment: the tick draws itself once. Nothing moves under reduced motion. */
export function DrawnTick({ className }: { className?: string }) {
  const ring = useRef<SVGCircleElement>(null);
  const tick = useRef<SVGPathElement>(null);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    ring.current?.animate([{ strokeDashoffset: 138 }, { strokeDashoffset: 0 }], { duration: 520, easing: "cubic-bezier(.2,.7,.2,1)", fill: "both" });
    tick.current?.animate([{ strokeDashoffset: 30 }, { strokeDashoffset: 30, offset: 0.45 }, { strokeDashoffset: 0 }], { duration: 760, easing: "ease-out", fill: "both" });
  }, []);
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
      <circle cx="24" cy="24" r="22" className="fill-accent-soft" />
      <circle ref={ring} cx="24" cy="24" r="22" fill="none" className="stroke-accent" strokeWidth="2.5" strokeDasharray="138" strokeLinecap="round" transform="rotate(-90 24 24)" />
      <path ref={tick} d="M15.5 24.5l6 6 11-12" fill="none" className="stroke-accent-text" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="30" />
    </svg>
  );
}

export function WelcomeStep({ workspace, name }: { workspace: string; name: string | null }) {
  const ws = useWorkspace(workspace);
  const mine = useMyOnboarding();
  const first = name?.trim().split(/\s+/)[0];
  const card = mine.data && ws.data && mine.data.workspace_id === ws.data.id ? mine.data.card : null;
  const needsCard = card?.status === "required" && !!card.setup_url;
  return (
    <div className="mx-auto max-w-2xl">
      <DrawnTick className="size-14" />
      <h1 className="mt-5 font-display text-3xl font-semibold tracking-tight text-text sm:text-4xl">{first ? `Welcome to Guv, ${first}` : "Welcome to Guv"}</h1>
      <div className="mt-2 text-md leading-relaxed text-muted">
        {ws.isPending ? (
          <Skeleton className="inline-block h-4 w-64 align-middle" />
        ) : (
          <>
            <span className="font-medium text-text">{ws.data?.name ?? "Your workspace"}</span> is ready, in the UK. Let's get your first app live: it usually takes about three minutes.
          </>
        )}
      </div>

      {needsCard && (
        <Callout
          tone="info"
          className="mt-6"
          title="Add a card before your first deploy"
          actions={
            <Button asChild size="sm">
              <a href={card.setup_url!}>
                Add a card
                <ArrowUpRight aria-hidden="true" />
              </a>
            </Button>
          }
        >
          {mine.data?.plan === "trial"
            ? "It keeps out people who'd misuse free computing. Nothing is charged during the trial."
            : "Your plan starts once there's a card on file. You'll get a VAT invoice each month."}
        </Callout>
      )}

      <div className="mt-8 grid gap-3 sm:grid-cols-2">
        <Choice
          workspace={workspace}
          via="github"
          icon={<GitHubMark />}
          title="Deploy from GitHub"
          body="Pick a repository. We work out how to build it and give you a live HTTPS address."
        />
        <Choice
          workspace={workspace}
          via="ai"
          icon={<Bot />}
          title="Deploy with your AI"
          body="Connect Claude Code, Cursor or Codex and ask it to deploy. Production changes still wait for you."
        />
      </div>

      <div className="mt-6 flex flex-col gap-4 rounded-xl border border-border bg-surface-2/60 p-4 sm:flex-row sm:items-center">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-surface text-muted" aria-hidden="true">
          <ListChecks className="size-[18px]" />
        </span>
        <p className="flex-1 text-sm text-muted">
          Not ready to deploy? Your workspace has a short checklist: a domain, a spend cap, your AI and your team. Pick it up whenever you like.
        </p>
        <Button asChild className="shrink-0">
          <Link to="/$workspace" params={{ workspace }}>
            Go to my workspace
          </Link>
        </Button>
      </div>

      <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted">
        {["Hosted in the UK", "HTTPS and backups on by default", "A spend cap from day one"].map((t) => (
          <li key={t} className="flex items-center gap-1.5">
            <Check className="size-4 text-ok-text" aria-hidden="true" />
            {t}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Choice({ workspace, via, icon, title, body }: { workspace: string; via: "github" | "ai"; icon: ReactNode; title: string; body: string }) {
  return (
    <Link
      to="/$workspace/new"
      params={{ workspace }}
      search={{ via }}
      className="group flex flex-col gap-2 rounded-xl border border-border bg-surface p-5 shadow-xs transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-px hover:border-border-strong hover:shadow-md focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-focus/40 motion-reduce:transition-none motion-reduce:hover:translate-y-0"
    >
      <span className="flex size-10 items-center justify-center rounded-lg bg-surface-2 text-text [&_svg]:size-5" aria-hidden="true">
        {icon}
      </span>
      <span className="mt-1 text-md font-semibold text-text">{title}</span>
      <span className="text-sm text-muted">{body}</span>
      <span className="mt-auto inline-flex items-center gap-1 pt-2 text-sm font-semibold text-accent-text">
        Start
        <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none" aria-hidden="true" />
      </span>
    </Link>
  );
}
